import { LoadingOutlined } from "@ant-design/icons";
import { Col, Input, InputNumber, Row, Typography } from "antd";
import { Formik } from "formik";
import { useRef } from "react";
import { createContext, useContext, useEffect, useReducer, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import {
  useForgotPasswordMutation,
  useForgotPasswordVerificationMutation,
  useResetPasswordMutation,
} from 'services/auth';
import { Button } from "shared/components/Button";
import { errors } from "shared/constants/errors";
import routes from "shared/constants/routes";
import { Header } from "shared/views/Header";
import * as Yup from 'yup';
import { useTranslation } from 'contexts/TranslationContext';
import hashedPaswword from "../utilities/hashedPassword";
import { buildPasswordValidation } from "../utilities/passwordValidation";
import { Label } from "Components";
import { HelpAndContact } from "./SignIn";
import HelpSupport from 'assets/svg/chat.svg'
import Call from 'assets/svg/call.svg'
import Email from 'assets/svg/email.svg'
import BgBottomLeftIcon from 'assets/svg/bgBottomLeft.svg'
import BgTopRightIcon from 'assets/svg/bgTopRight.svg'


const { Text } = Typography;

const ForgotPasswordContext = createContext(null);

const INITIAL_STATE = {
  step: 0,
  phone: '',
  password: '',
  otp: '',
};

const FLUSH = 'FLUSH';
const SET_PHONE = 'SET_PHONE';
const SET_OTP = 'SET_OTP';
const SET_PASSWORD = 'SET_PASSWORD';
const SET_STEP = 'SET_STEP';
const SET_TOKEN = 'SET_TOKEN';

const forgotPasswordReducer = (state, action) => {
  switch (action.type) {
    case FLUSH:
      return {
        ...INITIAL_STATE
      };
    case SET_PHONE:
      return {
        ...state,
        phone: action.payload,
      };
    case SET_OTP:
      return {
        ...state,
        otp: action.payload,
      };
    case SET_PASSWORD:
      return {
        ...state,
        password: action.payload,
      };
    case SET_STEP:
      return {
        ...state,
        step: action.payload,
      };
    case SET_TOKEN:
      return {
        ...state,
        token: action.payload,
      };
    default:
      return state;
  }
};

const Identification = () => {
  const { dispatch } = useContext(ForgotPasswordContext);
  const [forgotPassword, { isLoading }] = useForgotPasswordMutation();
  const { translate, language } = useTranslation();
  const schema = Yup.object().shape({
    phone: Yup.string()
      .length(9, 'Must be exactly 9 digits')
      .required('Cannot be empty'),
  });

  const onSubmit = async (values, { resetForm }) => {
    try {
      const response = await forgotPassword({
        ...values,
        phone: '966' + values?.phone
      }).unwrap();

      if (response?.isOTPSend) {
        dispatch({ type: SET_STEP, payload: 1 });
        dispatch({ type: SET_PHONE, payload: values.phone });
        toast.success('Credentials verified successfully');
      } else {
        resetForm();
        toast.error(response?.message || 'Phone number does not exist.');
      }
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  const d = language?.dir === 'rtl' ? { addonAfter: "+966" } : { addonBefore: "+966" };
  return (
    <>
      <Formik initialValues={{ phone: '' }} onSubmit={onSubmit} validationSchema={schema}>
        {({ handleSubmit, handleChange, values, errors }) => (
          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
            <div>
              <Label
                label={translate('FORGOT_PASSWORD.FORGOT_PASSWORD_LABEL')}
                required={true}
                name={translate('SIGN_IN.FIELDS.PASSWORD')}
              />
              <Input
                dir={"ltr"}
                {...d}
                status={errors.phone ? "error" : null}
                name="phone"
                id="phone"
                placeholder="5XXXXXXXX"
                onChange={handleChange}
                value={values.phone} />
              {
                errors.phone && <Text type="danger">{translate("FORGOT_PASSWORD." + errors.phone)}</Text>
              }
            </div>
            <Button type="submit" disabled={isLoading} block>
              {
                isLoading ? <LoadingOutlined /> : translate("FORGOT_PASSWORD.Send")
              }
            </Button>
            <Link className="text-primary text-center text-sm block font-bold" to={routes.AUTH.SIGN_IN}>
              <span>{translate('FORGOT_PASSWORD.Return to sign in')}</span>
            </Link>
          </form>
        )}
      </Formik>
    </>
  );
};

const OTP = () => {
  const { translate } = useTranslation();
  const [seconds, setSeconds] = useState(60);
  const [otp, setOtp] = useState(['', '', '', '']);

  const otpRef1 = useRef(null);
  const otpRef2 = useRef(null);
  const otpRef3 = useRef(null);
  const otpRef4 = useRef(null);

  const { state, dispatch } = useContext(ForgotPasswordContext);

  const [verify, { isLoading }] = useForgotPasswordVerificationMutation();

  const countdown = (intervalId) => {
    if (seconds <= 0) {
      dispatch({ type: SET_STEP, payload: 0 });
      toast.error('Timeout. Please, sign-in again');
      clearInterval(intervalId);
    } else {
      setSeconds(seconds - 1);
    }
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      countdown(intervalId);
    }, 1000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [seconds]); // eslint-disable-line

  const handleChange = (value, index) => {
    setOtp(otp => otp.map((_, i) => index === i ? value : _));

    switch (index) {
      case 0:
        otpRef2.current.focus();
        break;
      case 1:
        otpRef3.current.focus();
        break;
      case 2:
        otpRef4.current.focus();
        break;
      default:
        return;
    }
  };

  const onSubmit = async e => {
    e.preventDefault();

    try {
      const response = await verify({
        otp: otp.join(''),
        phone: '966' + state?.phone,
      }).unwrap();
      if (response?.verified) {
        dispatch({ type: SET_OTP, payload: otp.join('') });
        dispatch({ type: SET_STEP, payload: 2 });
        toast.success('Credentials verified successfully');
      } else {
        dispatch({ type: SET_STEP, payload: 0 });
        toast.error('Invalid OTP');
      }
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  const goBack = () => {
    dispatch({ type: FLUSH });
  };

  return (
    <>
      <div className="text-gray-400">{translate('FORGOT_PASSWORD.ONE_TIME_OTP')}</div>
      <form onSubmit={onSubmit} className="space-y-6 mt-6">
        <div className="flex justify-between w-full max-w-xl mx-auto" dir="ltr">
          {[otpRef1, otpRef2, otpRef3, otpRef4].map((ref, index) => (
            <input
              autoComplete="one-time-code"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              dir="ltr"
              key={index}
              ref={ref}
              maxLength={1}
              type='text'
              inputMode="numeric"
              onChange={(e) => {
                const value = e.target.value;
                if (/^\d$/.test(value)) {
                  handleChange(value, index);
                } else {
                  e.target.value = '';
                }
              }}
              onKeyDown={(e) => {
                if (
                  !/^\d$/.test(e.key) &&
                  e.key !== 'Backspace' &&
                  e.key !== 'Tab' &&
                  e.key !== 'ArrowLeft' &&
                  e.key !== 'ArrowRight'
                ) {
                  e.preventDefault();
                }
              }}
              onWheel={(e) => e.target.blur()} // disables mouse wheel input
              onPaste={(e) => e.preventDefault()} // disables pasting
              name={`otp${index + 1}`}
              id={`otp${index + 1}`}
              className="text-2xl font-semibold border border-gray-300 rounded-lg w-[22%] h-16 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 text-center no-spinner"
              required
            />
          ))}
        </div>


        <div className="text-center text-gray-400 font-bold text-3xl">
          00:{String(seconds).padStart(2, '0')}
        </div>

        <div className="text-center text-gray-400 font-bold text-sm cursor-not-allowed">
          {translate('SIGN_IN.RESEND_CODE')}
        </div>

        <Button type="submit" disabled={isLoading} block className="bg-yellow-400 hover:bg-yellow-500 text-white font-semibold text-base py-2 rounded-lg shadow">
          {isLoading ? <LoadingOutlined /> : translate('SIGN_IN.SEND')}
        </Button>
        <HelpAndContact />

      </form>
    </>
  );
};

const NewPassword = () => {
  const { state, dispatch } = useContext(ForgotPasswordContext);
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const navigate = useNavigate();
  const { translate } = useTranslation();


  const onSubmit = async (values, actions) => {
    try {
      const response = await resetPassword({
        phone: '966' + state?.phone,
        password: hashedPaswword(values.password),
        otp: state.otp,
      }).unwrap();

      if (response?.passwordUpdate) {
        toast.success('Password has been updated successfully');
        navigate(routes.AUTH.SIGN_IN);
      } else {
        toast.error('Password reset failed');
        dispatch({ type: SET_STEP, payload: 0 });
      }
    } catch (e) {
      toast.error('Something went wrong');
    }
  };

  const goBack = () => {
    dispatch({ type: FLUSH });
  };

  const schema = Yup.object().shape({
    password: buildPasswordValidation(translate),
    confirmPassword: Yup.string()
      .required(translate(errors.CANNOT_BE_EMPTY))
      .oneOf(
        [Yup.ref('password'), null],
        translate('VALIDATION.PASSWORDS_DO_NOT_MATCH')
      ),
  });

  return (
    <>
      <div className="text-gray-400">{translate('FORGOT_PASSWORD.ONE_TIME_PASS')}</div>
      <Formik onSubmit={onSubmit} initialValues={{ confirmPassword: '', password: '' }} validationSchema={schema}>
        {({ handleSubmit, handleChange, handleBlur, values, errors }) => (
          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
            <div>
              <Label
                label={translate('FORGOT_PASSWORD.PASSWORD')}
                required={true}
                name={translate('FORGOT_PASSWORD.PASSWORD')}
              />
              <Input.Password
                className={'tamweel-base-input'}
                status={errors.password ? "error" : null}
                name="password" id="password" placeholder="Password" onChange={handleChange} value={values.password} />
              {
                errors.password && <Text type="danger">{errors.password}</Text>
              }
            </div>
            <div>
              <Label
                label={translate('FORGOT_PASSWORD.CONFIRM_PASSWORD')}
                required={true}
                name={translate('FORGOT_PASSWORD.CONFIRM_PASSWORD')}
              />
              <Input.Password
                className={'tamweel-base-input'}
                status={errors.confirmPassword ? "error" : null}
                name="confirmPassword"
                id="confirmPassword"
                placeholder="Confirm password"
                onChange={handleChange}
                value={values.confirmPassword} />
              {
                errors.confirmPassword && <Text type="danger">{errors.confirmPassword}</Text>
              }
            </div>
            <Button type="submit" disabled={isLoading} block>
              {
                isLoading ? <LoadingOutlined /> : translate('FORGOT_PASSWORD.RESET')
              }
            </Button>
            <div onClick={goBack} className="cursor-pointer text-center text-primary font-bold text-sm">
              Back
            </div>
          </form>
        )}
      </Formik>
    </>
  );
};

export default function ForgotPassword() {
  const [state, dispatch] = useReducer(forgotPasswordReducer, INITIAL_STATE);
  const { translate } = useTranslation();
  return (
    <ForgotPasswordContext.Provider value={{ state, dispatch }}>
      <section className="relative h-screen bg-[#F3F4F6] overflow-hidden">
        <div className="absolute top-0 right-0 w-full md:w-2/5 lg:w-2/5 z-0">
          <img
            src={BgTopRightIcon}
            alt="Top Right Decoration"
            className="absolute top-0 right-0 w-full -z-10"
          />
        </div>

        {/* Bottom-left image */}
        <div className="absolute bottom-0 left-0 w-full md:w-2/5 lg:w-2/5 z-0">
          <img
            src={BgBottomLeftIcon}
            alt="Bottom Left Decoration"
            className="absolute bottom-0 left-0 w-full z-0"
          />
        </div>

        <Header />
        <div className="relative z-10 flex flex-col h-full pb-10">
          <div className="flex-1 overflow-auto  [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex flex-col items-center justify-center px-6 py-8 mx-auto min-h-full w-full">
              <div className="relative z-20 w-full bg-white rounded-3xl sm:max-w-2xl">
                <div className="p-6 shadow-md rounded-3xl space-y-4 md:space-y-6 sm:p-8 bg-[#FAFAFA]">
                  <h1 className="text-sm font-extrabold leading-tight tracking-tight text-primary uppercase md:text-2xl">
                    {translate("FORGOT_PASSWORD.Forgot password?")}
                  </h1>
                  <div className="bg-secondary w-16 h-1"></div>

                  {state?.step === 0 && <Identification />}
                  {state?.step === 1 && <OTP />}
                  {state?.step === 2 && <NewPassword />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </ForgotPasswordContext.Provider>
  );
}
